import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Music, Play, Pause, Download, Share2, Heart, Loader2 } from "lucide-react";
import WavesurferPlayer from "@wavesurfer/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORDER_POLL_INTERVAL_MS = 5000;

export default function ListenPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wavesurfer, setWavesurfer] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showLyrics, setShowLyrics] = useState(false);
  const [activeVariation, setActiveVariation] = useState(null);
  const [waveReady, setWaveReady] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderId}`);
        if (active) setOrder(data);
      } catch (err) {
        console.error("Failed to fetch order:", err);
        if (active) setOrder(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchOrder();
    const interval = setInterval(fetchOrder, ORDER_POLL_INTERVAL_MS);
    return () => { active = false; clearInterval(interval); };
  }, [orderId]);

  const onReady = useCallback((ws) => {
    setWavesurfer(ws);
    setDuration(ws.getDuration());
    setWaveReady(true);
  }, []);

  const onTimeUpdate = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const togglePlay = useCallback(() => {
    if (wavesurfer) wavesurfer.playPause();
  }, [wavesurfer]);

  const formatTime = (t) => {
    if (!t || isNaN(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `Custom Song for ${order?.quiz?.recipient_name}`, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  const switchVariation = useCallback((song) => {
    if (wavesurfer) {
      wavesurfer.load(song.audio_url);
      setActiveVariation(song.id);
      setPlaying(false);
      setWaveReady(false);
    }
  }, [wavesurfer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#2C2C2C] flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-[#7D1B1B] animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#2C2C2C] flex flex-col items-center justify-center px-4">
        <h1 className="font-display text-2xl font-bold text-white mb-4">Song Not Found</h1>
        <p className="text-white/50 mb-6">This song may not be ready yet or the link may be incorrect.</p>
        <button data-testid="back-home-btn" onClick={() => navigate("/")} className="bg-[#7D1B1B] text-white rounded-full px-6 py-3 font-bold">Back to Home</button>
      </div>
    );
  }

  const songReady = !!order.song_url;
  const recipientName = order.quiz?.recipient_name || "Someone Special";
  const genre = order.quiz?.genre || "Custom";
  const occasion = order.quiz?.occasion || "";
  const hasAlbumArt = !!order.song_image_url;
  const audioSrc = order.song_url || "";
  const allSongs = order.all_songs || [];

  return (
    <div className="min-h-screen bg-[#2C2C2C]">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center gap-2">
          <Music className="h-5 w-5 text-[#7D1B1B]" strokeWidth={1.5} />
          <span className="font-display text-lg font-bold text-white">StorytoSong</span>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-12">
        <div data-testid="song-player-container" className="text-center">
          {/* Album Art */}
          <div className="relative w-56 h-56 mx-auto mb-8">
            {hasAlbumArt ? (
              <div className={`w-full h-full rounded-full overflow-hidden shadow-2xl shadow-[#7D1B1B]/20 ${playing ? "animate-spin" : ""}`}
                style={{ animationDuration: "8s" }}>
                <img src={order.song_image_url} alt="Song cover" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-[#2C2C2C]/80 flex items-center justify-center border-4 border-white/10">
                    <div className="w-3 h-3 rounded-full bg-[#7D1B1B]" />
                  </div>
                </div>
              </div>
            ) : (
              <div className={`w-full h-full rounded-full bg-gradient-to-br from-[#7D1B1B] to-[#9B2C2C] shadow-2xl flex items-center justify-center ${playing ? "animate-spin" : ""}`}
                style={{ animationDuration: "8s" }}>
                <div className="w-20 h-20 rounded-full bg-[#2C2C2C] flex items-center justify-center border-4 border-white/10">
                  <div className="w-3 h-3 rounded-full bg-[#7D1B1B]" />
                </div>
                <div className="absolute inset-8 rounded-full border border-white/5" />
                <div className="absolute inset-12 rounded-full border border-white/5" />
                <div className="absolute inset-16 rounded-full border border-white/5" />
              </div>
            )}
          </div>

          {/* Song Info */}
          <h1 data-testid="song-title" className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
            A Song for {recipientName}
          </h1>
          <p className="text-white/60 text-sm mb-1">{genre} {occasion ? `\u00B7 ${occasion}` : ""}</p>
          <p className="text-white/40 text-xs mb-8">by StorytoSong</p>

          {songReady && (
            <>
              {/* Wavesurfer Player */}
              <div data-testid="waveform-player" className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10 space-y-5">
                {/* Waveform */}
                <div className="relative">
                  {!waveReady && (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 text-[#7D1B1B] animate-spin mr-2" />
                      <span className="text-white/40 text-sm">Loading waveform...</span>
                    </div>
                  )}
                  <div style={{ opacity: waveReady ? 1 : 0, transition: "opacity 0.4s ease" }}>
                    <WavesurferPlayer
                      height={64}
                      waveColor="rgba(255,255,255,0.2)"
                      progressColor="#7D1B1B"
                      cursorColor="#9B2C2C"
                      cursorWidth={2}
                      barWidth={2}
                      barGap={2}
                      barRadius={2}
                      url={audioSrc}
                      onReady={onReady}
                      onPlay={() => setPlaying(true)}
                      onPause={() => setPlaying(false)}
                      onFinish={() => setPlaying(false)}
                      onTimeupdate={onTimeUpdate}
                    />
                  </div>
                </div>

                {/* Time display */}
                <div className="flex justify-between text-xs text-white/40 -mt-2">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>

                {/* Play button + controls */}
                <div className="flex items-center justify-center gap-6">
                  <button data-testid="play-pause-btn" onClick={togglePlay}
                    className="w-16 h-16 rounded-full bg-[#7D1B1B] flex items-center justify-center hover:bg-[#9B2C2C] transition-all transform hover:scale-105 shadow-xl shadow-[#7D1B1B]/30">
                    {playing ? <Pause className="h-7 w-7 text-white" /> : <Play className="h-7 w-7 text-white ml-1" />}
                  </button>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-center gap-3 pt-1">
                  <button data-testid="share-btn" onClick={handleShare}
                    className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors px-4 py-2 rounded-full bg-white/5 hover:bg-white/10">
                    <Share2 className="h-4 w-4" /> Share
                  </button>
                  <a data-testid="download-btn" href={order.song_url} download
                    className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors px-4 py-2 rounded-full bg-white/5 hover:bg-white/10">
                    <Download className="h-4 w-4" /> Download
                  </a>
                  <button data-testid="lyrics-btn" onClick={() => setShowLyrics(!showLyrics)}
                    className={`flex items-center gap-2 text-sm transition-colors px-4 py-2 rounded-full ${showLyrics ? "bg-[#7D1B1B]/20 text-[#D4A853] border border-[#7D1B1B]/30" : "text-white/60 hover:text-white bg-white/5 hover:bg-white/10"}`}>
                    <Music className="h-4 w-4" /> Lyrics
                  </button>
                </div>
              </div>

              {/* Lyrics */}
              {showLyrics && order.lyrics && (
                <div data-testid="lyrics-panel" className="mt-6 bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 text-left animate-fade-in">
                  <h3 className="font-display text-lg font-bold text-white mb-4">Lyrics</h3>
                  <pre className="text-white/70 text-sm whitespace-pre-wrap font-body leading-relaxed">{order.lyrics}</pre>
                </div>
              )}

              {/* Song Variations */}
              {allSongs.length > 1 && (
                <div data-testid="song-variations" className="mt-6 bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 text-left animate-fade-in">
                  <h3 className="font-display text-lg font-bold text-white mb-4">Song Variations</h3>
                  <p className="text-white/50 text-xs mb-4">We created {allSongs.length} versions. Listen to each and pick your favorite.</p>
                  <div className="space-y-3">
                    {allSongs.map((song, idx) => {
                      const isActive = activeVariation ? activeVariation === song.id : idx === 0;
                      return (
                        <button key={song.id || idx} data-testid={`variation-${idx}`}
                          onClick={() => switchVariation(song)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive ? "bg-[#7D1B1B]/20 border border-[#7D1B1B]/30" : "bg-white/5 hover:bg-white/10"}`}>
                          <Play className="h-4 w-4 text-[#7D1B1B] flex-shrink-0" />
                          <span className="text-white text-sm">Version {idx + 1}</span>
                          <span className="text-white/40 text-xs ml-auto">
                            {song.duration ? `${Math.floor(song.duration / 60)}:${String(Math.floor(song.duration % 60)).padStart(2, "0")}` : ""}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {!songReady && order.status === "generation_failed" && (
            <div data-testid="song-failed" className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <Music className="h-8 w-8 text-red-400" />
              </div>
              <p className="text-white font-bold">Song generation encountered an issue</p>
              <p className="text-white/60 text-sm">Our team has been notified. We&apos;ll get this sorted and update you via email. {order.kie_ai_error ? `Error: ${order.kie_ai_error}` : ""}</p>
            </div>
          )}

          {!songReady && order.status !== "generation_failed" && (
            <div data-testid="song-not-ready" className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 space-y-4">
              <Loader2 className="h-10 w-10 text-[#7D1B1B] animate-spin mx-auto" />
              <p className="text-white font-bold">Your song is being produced...</p>
              <p className="text-white/60 text-sm">Our AI is composing and producing your custom song. This usually takes 1-3 minutes. This page will update automatically.</p>
            </div>
          )}

          {/* Love note */}
          <div className="mt-8 text-center">
            <Heart className="h-5 w-5 text-[#7D1B1B] mx-auto mb-2" />
            <p className="text-white/40 text-xs">Made with love by StorytoSong</p>
          </div>
        </div>
      </div>
    </div>
  );
}
